/**
 * @module editablefield/editablefieldui
 */

import ContainerElement from '@ckeditor/ckeditor5-engine/src/view/containerelement';
import Position from '@ckeditor/ckeditor5-engine/src/view/position';
import { Plugin } from 'ckeditor5/src/core';
import { ClickObserver } from 'ckeditor5/src/engine';
import { ButtonView, clickOutsideHandler, ContextualBalloon } from 'ckeditor5/src/ui';
import { isWidget } from 'ckeditor5/src/widget';
import editablefieldIcon from '../theme/icons/editablefield.svg';
import EditableFieldActionsView from './ui/editablefieldactionsview';
import EditableFieldFormView from './ui/editablefieldformview';
import { isEditableFieldElement } from './utils';

/**
 * The editablefield UI plugin. It introduces the `'editablefield'` and `'uneditablefield'` buttons.
 *
 * It uses the {@link module:ui/panel/balloon/contextualballoon~ContextualBalloon contextual balloon plugin}.
 *
 * @extends {Plugin}
 */
export default class EditableFieldUI extends Plugin {
	/**
	 * @inheritDoc
	 */
	static get requires() {
		return [ ContextualBalloon ];
	}

	/**
	 * @inheritDoc
	 */
	static get pluginName() {
		return 'EditableFieldUI';
	}

	/**
	 * @inheritDoc
	 */
	init() {
		const editor = this.editor;

		editor.editing.view.addObserver( ClickObserver );

		this.actionsView = this._createActionsView();
		this.formView = this._createFormView();

		/**
		 * The contextual balloon plugin instance.
		 *
		 * @private
		 * @type {ContextualBalloon}
		 */
		this._balloon = editor.plugins.get( ContextualBalloon );

		this._createToolbarEditableFieldButton();
		this._enableUserBalloonInteractions();
	}

	/**
	 * @inheritDoc
	 */
	destroy() {
		super.destroy();
		this.formView.destroy();
	}

	/**
	 * Creates the {@link module:editablefield/ui/editablefieldactionsview~EditableFieldActionsView} instance.
	 *
	 * @private
	 * @returns {EditableFieldActionsView} The editablefield actions view instance.
	 */
	_createActionsView() {
		const editor = this.editor;
		const actionsView = new EditableFieldActionsView( editor.locale );
		const editableFieldCommand = editor.commands.get( 'editablefield' );
		const unEditableFieldCommand = editor.commands.get( 'uneditablefield' );

		actionsView.bind( 'placeholder' ).to( editableFieldCommand );
		actionsView.editButtonView.bind( 'isEnabled' ).to( editableFieldCommand );
		actionsView.unEditableFieldButtonView.bind( 'isEnabled' ).to( unEditableFieldCommand );

		// Execute editablefield command after clicking on the "Edit" button.
		this.listenTo( actionsView, 'edit', () => {
			this._addFormView();
		} );

		// Execute uneditablefield command after clicking on the "Uneditablefield" button.
		this.listenTo( actionsView, 'uneditablefield', () => {
			editor.execute( 'uneditablefield' );
			this._hideUI();
		} );

		// Close the panel on esc key press when the **actions have focus**.
		actionsView.keystrokes.set( 'Esc', ( data, cancel ) => {
			this._hideUI();
			cancel();
		} );

		return actionsView;
	}

	/**
	 * Creates the {@link module:editablefield/ui/editablefieldformview~EditableFieldFormView} instance.
	 *
	 * @private
	 * @returns {EditableFieldFormView} The editablefield form instance.
	 */
	_createFormView() {
		const editor = this.editor;
		const editablefieldCommand = editor.commands.get( 'editablefield' );

		const formView = new EditableFieldFormView( editor.locale );

		formView.valueInputView.bind( 'isReadOnly' ).to( editablefieldCommand, 'isEnabled', value => !value );
		formView.placeholderInputView.bind( 'isReadOnly' ).to( editablefieldCommand, 'isEnabled', value => !value );
		formView.saveButtonView.bind( 'isEnabled' ).to( editablefieldCommand );

		// Execute editablefield command after clicking the "Save" button.
		this.listenTo( formView, 'submit', () => {
			const newAttributes = {};
			const oldValue = editablefieldCommand.value || '';
			const oldPlaceholder = editablefieldCommand.placeholder || '';
			const newValue = formView.valueInputView.fieldView.element.value;
			const newPlaceholder = formView.placeholderInputView.fieldView.element.value;
			if ( oldValue != newValue ) {
				newAttributes.value = newValue;
			}
			if ( oldPlaceholder != newPlaceholder ) {
				newAttributes.placeholder = newPlaceholder;
			}

			if ( newAttributes.value != null || newAttributes.placeholder != null ) {
				editor.execute( 'editablefield', newAttributes );
			} else if ( oldValue != null || oldPlaceholder != null ) {
				editor.execute( 'uneditablefield' );
			}
			this._closeFormView();
		} );

		// Hide the panel after clicking the "Cancel" button.
		this.listenTo( formView, 'cancel', () => {
			this._closeFormView();
		} );

		// Close the panel on esc key press when the **form has focus**.
		formView.keystrokes.set( 'Esc', ( data, cancel ) => {
			this._closeFormView();
			cancel();
		} );

		return formView;
	}

	/**
	 * Creates a toolbar EditableField button. Clicking this button will show
	 * a {@link #_balloon} attached to the selection.
	 *
	 * @private
	 */
	_createToolbarEditableFieldButton() {
		const editor = this.editor;
		const editableFieldCommand = editor.commands.get( 'editablefield' );
		const t = editor.t;

		editor.ui.componentFactory.add( 'editablefield', locale => {
			const button = new ButtonView( locale );

			button.isEnabled = true;
			button.label = t( 'Campo' );
			button.icon = editablefieldIcon;
			button.tooltip = true;
			button.isToggleable = true;

			// Bind button to the command.
			button.bind( 'isEnabled' ).to( editableFieldCommand, 'isEnabled' );
			button.bind( 'isOn' ).to( editableFieldCommand, 'value', value => !!value );

			// Show the panel on button click.
			this.listenTo( button, 'execute', () => this._showUI( true ) );

			return button;
		} );
	}

	/**
	 * Attaches actions that control whether the balloon panel containing the
	 * {@link #formView} is visible or not.
	 *
	 * @private
	 */
	_enableUserBalloonInteractions() {
		const viewDocument = this.editor.editing.view.document;

		// Handle click on view document and show panel when selection is placed inside the editablefield element.
		// Keep panel open until selection will be inside the same editablefield element.
		this.listenTo( viewDocument, 'click', () => {
			const elementEditableField = this._getSelectedEditableFieldElement();

			if ( elementEditableField ) {
				// Then show panel but keep focus inside editor editable.
				this._showUI();
			}
		} );

		// Focus the form if the balloon is visible and the Tab key has been pressed.
		this.editor.keystrokes.set( 'Tab',
			( data, cancel ) => {
				if ( this._areActionsVisible && !this.actionsView.focusTracker.isFocused ) {
					this.actionsView.focus();
					cancel();
				}
			}, {
			priority: 'high'
		} );

		// Close the panel on the Esc key press when the editable has focus and the balloon is visible.
		this.editor.keystrokes.set( 'Esc', ( data, cancel ) => {
			if ( this._isUIVisible ) {
				this._hideUI();
				cancel();
			}
		} );

		// Close on click outside of balloon panel element.
		clickOutsideHandler( {
			emitter: this.formView,
			activator: () => this._isUIVisible,
			contextElements: [ this._balloon.view.element ],
			callback: () => this._hideUI()
		} );
	}

	/**
	 * Adds the {@link #actionsView} to the {@link #_balloon}.
	 *
	 * @protected
	 */
	_addActionsView() {
		if ( this._areActionsInPanel ) {
			return;
		}

		this._balloon.add( {
			view: this.actionsView,
			position: this._getBalloonPositionData()
		} );
	}

	/**
	 * Adds the {@link #formView} to the {@link #_balloon}.
	 *
	 * @protected
	 */
	_addFormView( initialValue = '' ) {
		if ( this._isFormInPanel ) {
			return;
		}

		const editableFieldCommand = this.editor.commands.get( 'editablefield' );

		this.formView.disableCssTransitions();

		this._balloon.add( {
			view: this.formView,
			position: this._getBalloonPositionData()
		} );

		if ( this._balloon.visibleView === this.formView ) {
			this.formView.valueInputView.fieldView.select();
		}

		this.formView.enableCssTransitions();

		this.formView.valueInputView.fieldView.element.value = editableFieldCommand.value || initialValue;
		this.formView.placeholderInputView.fieldView.element.value = editableFieldCommand.placeholder || '';
	}

	/**
	 * Removes the {@link #formView} from the {@link #_balloon}.
	 *
	 * @protected
	 */
	_closeFormView() {
		const editableFieldCommand = this.editor.commands.get( 'editablefield' );

		if ( editableFieldCommand.value != null || editableFieldCommand.placeholder != null ) {
			this._removeFormView();
		} else {
			this._hideUI();
		}
	}

	/**
	 * Removes the {@link #formView} from the {@link #_balloon}.
	 *
	 * @protected
	 */
	_removeFormView() {
		if ( this._isFormInPanel ) {
			// Blur the input element before removing it from DOM to prevent issues in some browsers.
			// See https://github.com/ckeditor/ckeditor5/issues/1501.
			this.formView.saveButtonView.focus();

			this._balloon.remove( this.formView );

			// Because the form has an input which has focus, the focus must be brought back
			// to the editor. Otherwise, it would be lost.
			this.editor.editing.view.focus();
		}
	}

	/**
	 * Shows the right kind of the UI for current state of the command. It's either
	 * {@link #formView} or {@link #actionsView}.
	 *
	 * @param {Boolean} forceVisible
	 * @private
	 */
	_showUI( forceVisible = false ) {
		// When there's no editableField under the selection, go straight to the editing UI.
		if ( !this._getSelectedEditableFieldElement() ) {
			this._addActionsView();

			// Be sure panel with editablefield is visible.
			if ( forceVisible ) {
				this._balloon.showStack( 'main' );
			}

			this._addFormView( Array.from( this.editor.model.document.selection.getFirstRange().getItems() ).map( v => v.data ).join( '' ) );
		}
		// If there's a editableField under the selection...
		else {
			// Go to the editing UI if actions are already visible.
			if ( this._areActionsVisible ) {
				this._addFormView();
			}
			// Otherwise display just the actions UI.
			else {
				this._addActionsView();
			}

			// Be sure panel with editablefield is visible.
			if ( forceVisible ) {
				this._balloon.showStack( 'main' );
			}
		}

		// Begin responding to ui#update once the UI is added.
		this._startUpdatingUI();
	}

	/**
	 * Removes the {@link #formView} from the {@link #_balloon}.
	 *
	 * See {@link #_addFormView}, {@link #_addActionsView}.
	 *
	 * @protected
	 */
	_hideUI() {
		if ( !this._isUIInPanel ) {
			return;
		}

		const editor = this.editor;

		this.stopListening( editor.ui, 'update' );
		this.stopListening( this._balloon, 'change:visibleView' );

		// Make sure the focus always gets back to the editable _before_ removing the focused form view.
		// Doing otherwise causes issues in some browsers. See https://github.com/ckeditor/ckeditor5-link/issues/193.
		editor.editing.view.focus();

		// Remove form first because it's on top of the stack.
		this._removeFormView();

		// Then remove the actions view because it's beneath the form.
		this._balloon.remove( this.actionsView );
	}

	/**
	 * Makes the UI react to the {@link module:core/editor/editorui~EditorUI#event:update} event to
	 * reposition itself when the editor ui should be refreshed.
	 *
	 * See: {@link #_hideUI} to learn when the UI stops reacting to the `update` event.
	 *
	 * @protected
	 */
	_startUpdatingUI() {
		const editor = this.editor;
		const viewDocument = editor.editing.view.document;

		let prevSelectedEditableField = this._getSelectedEditableFieldElement();
		let prevSelectionParent = getSelectionParent();

		const update = () => {
			const selectedEditableField = this._getSelectedEditableFieldElement();
			const selectionParent = getSelectionParent();

			// Hide the panel if:
			//
			// * the selection went out of the EXISTING editablefield element. E.g. user moved the caret out
			//   of the editablefield,
			// * the selection went to a different parent when creating a NEW editablefield. E.g. someone
			//   else modified the document.
			// * the selection has expanded (e.g. displaying editablefield actions then pressing SHIFT+Right arrow).
			//
			// Note: #_getSelectedEditableFieldElement will return a editablefield for a non-collapsed selection only
			// when fully selected.
			if ( ( prevSelectedEditableField && !selectedEditableField ) ||
				( !prevSelectedEditableField && selectionParent !== prevSelectionParent ) ) {
				this._hideUI();
			}
			// Update the position of the panel when:
			//  * editablefield panel is in the visible stack
			//  * the selection remains in the original editablefield element,
			//  * there was no editablefield element in the first place, i.e. creating a new editablefield
			else if ( this._isUIVisible ) {
				// If still in a editablefield element, simply update the position of the balloon.
				// If there was no editablefield (e.g. inserting one), the balloon must be moved
				// to the new position in the editing view (a new native DOM range).
				this._balloon.updatePosition( this._getBalloonPositionData() );
			}

			prevSelectedEditableField = selectedEditableField;
			prevSelectionParent = selectionParent;
		};

		function getSelectionParent() {
			return viewDocument.selection.focus.getAncestors()
				.reverse()
				.find( node => node.is( 'element' ) );
		}

		this.listenTo( editor.ui, 'update', update );
		this.listenTo( this._balloon, 'change:visibleView', update );
	}

	/**
	 * Returns true when {@link #formView} is in the {@link #_balloon}.
	 *
	 * @readonly
	 * @protected
	 * @type {Boolean}
	 */
	get _isFormInPanel() {
		return this._balloon.hasView( this.formView );
	}

	/**
	 * Returns true when {@link #actionsView} is in the {@link #_balloon}.
	 *
	 * @readonly
	 * @protected
	 * @type {Boolean}
	 */
	get _areActionsInPanel() {
		return this._balloon.hasView( this.actionsView );
	}

	/**
	 * Returns true when {@link #actionsView} is in the {@link #_balloon} and it is
	 * currently visible.
	 *
	 * @readonly
	 * @protected
	 * @type {Boolean}
	 */
	get _areActionsVisible() {
		return this._balloon.visibleView === this.actionsView;
	}

	/**
	 * Returns true when {@link #actionsView} or {@link #formView} is in the {@link #_balloon}.
	 *
	 * @readonly
	 * @protected
	 * @type {Boolean}
	 */
	get _isUIInPanel() {
		return this._isFormInPanel || this._areActionsInPanel;
	}

	/**
	 * Returns true when {@link #actionsView} or {@link #formView} is in the {@link #_balloon} and it is
	 * currently visible.
	 *
	 * @readonly
	 * @protected
	 * @type {Boolean}
	 */
	get _isUIVisible() {
		const visibleView = this._balloon.visibleView;

		return visibleView == this.formView || this._areActionsVisible;
	}

	/**
	 * Returns positioning options for the {@link #_balloon}. They control the way the balloon is attached
	 * to the target element or selection.
	 *
	 * If the selection is collapsed and inside a editablefield element, the panel will be attached to the
	 * entire editablefield element. Otherwise, it will be attached to the selection.
	 *
	 * @private
	 * @returns {Options}
	 */
	_getBalloonPositionData() {
		const view = this.editor.editing.view;
		const viewDocument = view.document;
		const target = () => {
			const targetEditableField = this._getSelectedEditableFieldElement();

			return targetEditableField ?
				// When selection is inside editablefield element, then attach panel to this element.
				view.domConverter.mapViewToDom( targetEditableField ) :
				// Otherwise attach panel to the selection.
				view.domConverter.viewRangeToDom( viewDocument.selection.getFirstRange() );
		};

		return { target };
	}

	/**
	 * Returns the editablefield under the  editing view's selection or `null` if there is none.
	 *
	 * **Note**: For a non–collapsed selection the editablefield element is only returned when **fully**
	 * selected and the **only** element within the selection boundaries.
	 *
	 * @private
	 * @returns {ContainerElement}
	 */
	_getSelectedEditableFieldElement() {
		const view = this.editor.editing.view;
		const selection = view.document.selection;
		const selectedElement = selection.getSelectedElement();

		// The selection is collapsed or some widget is selected (especially inline widget).
		if ( selectedElement && isWidget( selectedElement ) && isEditableFieldElement( selectedElement ) ) {
			return selectedElement;
		} else if ( selection.isCollapsed || selectedElement && isWidget( selectedElement ) ) {
			return findEditableFieldElementAncestor( selection.getFirstPosition() );
		} else {
			// The range for fully selected editablefield is usually anchored in adjacent text nodes.
			// Trim it to get closer to the actual editablefield element.
			const range = selection.getFirstRange().getTrimmed();
			const startEditableField = findEditableFieldElementAncestor( range.start );
			const endEditableField = findEditableFieldElementAncestor( range.end );

			if ( !startEditableField || startEditableField != endEditableField ) {
				return null;
			}

			// Check if the editablefield element is fully selected.
			if ( view.createRangeIn( startEditableField ).getTrimmed().isEqual( range ) ) {
				return startEditableField;
			} else {
				return null;
			}
		}
	}
}

/**
 * Returns a editablefield element if there's one among the ancestors of the provided `Position`.
 *
 * @param {Position} position
 * @returns
 */
function findEditableFieldElementAncestor( position ) {
	return position.getAncestors().find( ancestor => isEditableFieldElement( ancestor ) );
}
